/**
 * API Configuration Utility
 * Determines the correct backend URLs based on the current environment
 * 
 * DATA SOURCES:
 * - Draaidagen: Airtable (created/managed there)
 * - Materialen: Airtable (created/managed there)
 * - Reserveringen: PostgreSQL (faster, no rate limits)
 * - Junctions: PostgreSQL (faster, no rate limits)
 */

// Detect environment and set appropriate URLs
const isLocalhost = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';
const isTest = window.location.hostname === 'test.fullframe.nl';

// Geexporteerd voor de TestEnvironmentBanner — test heeft een eigen database met demodata.
export const isTestEnvironment = isTest;

// Backend URL (Node.js Express server - Airtable proxy)
export const BACKEND_URL = isLocalhost
  ? 'http://localhost:8080'
  : isTest
    ? 'https://ff-interne-backend-test.bluewater-2c879537.westeurope.azurecontainerapps.io'
    : 'https://ff-interne-backend.bluewater-2c879537.westeurope.azurecontainerapps.io';

// AI Engine URL (Python FastAPI server - PostgreSQL)
export const AI_ENGINE_URL = (isLocalhost || isTest)
  ? 'https://ff-interne-ai-engine-test.bluewater-2c879537.westeurope.azurecontainerapps.io'
  : 'https://ff-interne-ai-engine.bluewater-2c879537.westeurope.azurecontainerapps.io';

// All API calls go through the backend (which proxies to AI Engine/PostgreSQL as needed)
const USE_POSTGRES_DRAAIDAGEN = false;    // Draaidagen -> Airtable (created there)
const USE_POSTGRES_MATERIALS = false;     // Materials -> Airtable (created there)

// Legacy flag for backward compatibility
export const USE_POSTGRES_BACKEND = true;

export const API_ENDPOINTS = {
  // Draaidagen - ALWAYS from Airtable (they are created/managed there)
  draaidagen: {
    list: () => USE_POSTGRES_DRAAIDAGEN 
      ? `${AI_ENGINE_URL}/api/v1/draaidagen/` 
      : `${BACKEND_URL}/Api/draaidagen`,
    get: (id) => USE_POSTGRES_DRAAIDAGEN 
      ? `${AI_ENGINE_URL}/api/v1/draaidagen/${id}` 
      : `${BACKEND_URL}/Api/draaidagen/${id}`,
    update: (id) => USE_POSTGRES_DRAAIDAGEN 
      ? `${AI_ENGINE_URL}/api/v1/draaidagen/${id}` 
      : `${BACKEND_URL}/Api/draaidagen/${id}`,
  },
  
  // Materials - ALWAYS from Airtable (they are created/managed there)
  materials: {
    list: () => USE_POSTGRES_MATERIALS 
      ? `${AI_ENGINE_URL}/api/v1/materials/` 
      : `${BACKEND_URL}/Api/materialen`,
    get: (id) => USE_POSTGRES_MATERIALS 
      ? `${AI_ENGINE_URL}/api/v1/materials/${id}` 
      : `${BACKEND_URL}/Api/materialen/${id}`,
    batch: () => USE_POSTGRES_MATERIALS 
      ? `${AI_ENGINE_URL}/api/v1/materials/batch` 
      : `${BACKEND_URL}/Api/materialen/batch`,
    resolveOldBase: () => USE_POSTGRES_MATERIALS 
      ? `${AI_ENGINE_URL}/api/v1/materials/resolve/old-base` 
      : `${BACKEND_URL}/Api/materialen/resolve-old-base`,
  },
  
  // Reservations - always via backend (adds auth, enables caching)
  reservations: {
    list: () => `${BACKEND_URL}/Api/reservations`,
    get: (id) => `${BACKEND_URL}/Api/reservations/${id}`,
    update: (id) => `${BACKEND_URL}/Api/reservations/${id}`,
    save: () => `${BACKEND_URL}/Api/save-reservation`,
    delete: (id) => `${BACKEND_URL}/Api/reservations/${id}`,
    updateNotitie: (id) => `${BACKEND_URL}/Api/reservations/${id}/notitie`,
    checkAvailability: () => `${BACKEND_URL}/Api/check-availability`,
  },
  
  // Junctions - PostgreSQL for better performance
  junctions: {
    list: () => `${BACKEND_URL}/Api/junctions`,
    create: () => `${BACKEND_URL}/Api/junctions`,
    update: (id) => `${BACKEND_URL}/Api/junctions/${id}`,
    existing: () => `${BACKEND_URL}/Api/junctions/existing`,
  },
  
  // Combined data - uses PostgreSQL for reservations/junctions, Airtable for materials
  draaidagData: (id) => `${BACKEND_URL}/Api/draaidag-data/${id}`,
};

// Helper function for backward compatibility with REACT_APP_CURRENT_URL
export const getBackendUrl = () => BACKEND_URL;

// Log configuration on load (development only)
if (isLocalhost) {
  console.log('[API Config] Environment: localhost');
  console.log('[API Config] Backend URL:', BACKEND_URL);
  console.log('[API Config] AI Engine URL:', AI_ENGINE_URL);
  console.log('[API Config] Using PostgreSQL backend:', USE_POSTGRES_BACKEND);
}
